import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, Output, Renderer2, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

import { util } from '../../../classes/util';
import { ESRI } from '../../../esri-loader-util';
import { BooleanConverter, InputConverter, JSONConverter, NumberConverter } from '../../../inputConverter';
import { GisToolsService } from '../../../services/gis-tools.service';
import { VideoLogService, ZoomOnPositionChangeType } from '../../../services/video-log.service';
import { CRSList, LocalCRSList } from '../../../types';
import { BaseComponent } from '../../base.component';

let Graphic: __esri.GraphicConstructor;
let WebMercatorUtils: __esri.webMercatorUtils;

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-video-log-viewer-ng',
    templateUrl: './video-log-viewer.component.html',
    styleUrls: ['../../sharedStyles/base.scss', './video-log-viewer.component.scss']
})
export class VideoLogViewerComponent extends BaseComponent implements AfterViewInit {
    @Input() mapViewRef: NonNullable<__esri.MapView>;
    videoLogViewerHandler;
    @Input()
    get active(): boolean | string { return this._active; }
    set active(value: boolean | string) {
        BooleanConverter(value) ? this.activate() : this.deactivate();
    }
    private _active;

    private readonly latLongDebounce = 100;

    private firstZoom = false;
    @Input()
    get zoomOnPositionChange(): boolean | ZoomOnPositionChangeType {
        return this.videoLogService.zoomOnPositionChange;
    }
    set zoomOnPositionChange(val: boolean | ZoomOnPositionChangeType) {
        if (typeof val === 'string') {
            val = val.toLocaleLowerCase().trim() as ZoomOnPositionChangeType;
            if ((val as string) === 'true') {
                val = 'follow';
            } else if ((val as string) === 'false') {
                val = 'none';
            }
        } else {
            if (val) {
                val = 'follow';
            } else {
                val = 'none';
            }
        }
        this.videoLogService.zoomOnPositionChange = val;
    }

    @Input()
    get lat(): number { return this._lat; }
    set lat(value: number) {
        this._lat = NumberConverter(value);
        this.executePositionChange();
    }
    @Input()
    @InputConverter(NumberConverter)
    get long(): number { return this._long; }
    set long(value: number) {
        this._long = value;
        this.executePositionChange();
    }
    private _lat: number;
    private _long: number;
    private readonly executePositionChange: () => void;

    @Input() aoiString: string = '';

    @Input() @InputConverter(NumberConverter) precision: number = 5;
    @Input() @InputConverter(NumberConverter) buffer: number = 50;

    @Input() directionalMarker: boolean = false;
    @Input()
    @InputConverter(JSONConverter)
    markerSymbol: any = {};

    @Output() public readonly positionChange = new EventEmitter<{ lat: number; long: number; crsList: CRSList; localCRSList: LocalCRSList }>();
    markerGraphic: __esri.Graphic;

    @Output() public readonly openPopUp = new EventEmitter<string>();

    @Output() public readonly loadHistoricReport = new EventEmitter<void>();

    @Output() public readonly refreshViewer = this.videoLogService.refreshViewer;

    @Output() public readonly swapPanelEvent = new EventEmitter<void>();
    @Input() threeImageViewEnabled: boolean = false;
    public showThreeImages = false;
    public hideSwapBtn = true;
    public prevX = [0, 0, 0];
    public prevY = [0, 0, 0];
    public topPx = '25px';
    public bottomPx = '';
    public showLeft = true;
    public showCenter = true;
    public showRight = true;
    public hidePopUpButtons = false;

    @Input() @InputConverter(NumberConverter) zoomFactor: number = 0.1; // Adjust this value to control the zoom factor
    private currentScale: number[] = [1, 1, 1];

    @Input()
    get mapClickActive(): boolean { return this._mapClickActive; }
    set mapClickActive(value: boolean) {
        if (this._mapClickActive !== value) {
            this._mapClickActive = value;
            this.setClickAction();
        }
    }
    private _mapClickActive = true;

    @Input()
    get followZoomLevel(): number {
        return this.videoLogService.followZoomLevel;
    }
    set followZoomLevel(val: number) {
        this.videoLogService.followZoomLevel = NumberConverter(val);
    }

    private offsetSubscription: Subscription;
    private readonly followZoomDebounce = 10;

    @Input() enablePopUpWindow: boolean = false;
    @Input() toolTipText: string = '';

    private dragEventsSet = false;
    public imageBeingDragged: HTMLDivElement;

    public isDragging = false;
    private initialX: number = 0;
    private initialY: number = 0;

    constructor(
        _vcf: ViewContainerRef,
        private readonly gisToolsService: GisToolsService,
        public readonly videoLogService: VideoLogService,
        private readonly cdr: ChangeDetectorRef,
        private readonly renderer: Renderer2
    ) {
        super(_vcf, true);
        this.goToMarker = util.debounce(() => {
            // Have to use any because ESRI's types are screwed up
            const goToOptions: any = {
                target: this.markerGraphic,
                animate: true
            };
            if ((typeof this.zoomOnPositionChange === 'string' && this.zoomOnPositionChange
                && (this.zoomOnPositionChange === 'zoom' || (this.zoomOnPositionChange === 'first' && this.firstZoom)))
                || (typeof this.zoomOnPositionChange === 'boolean' && this.zoomOnPositionChange)) {
                this.firstZoom = false;
                goToOptions.zoom = this.followZoomLevel;
            }
            this.mapViewRef.goTo(goToOptions);
        }, this.followZoomDebounce);
        this.executePositionChange = util.debounce(() => {
            this.onPositionChange(true);
        }, this.latLongDebounce);
    }

    @ViewChild('middleImage') private readonly middleImageRef: ElementRef<HTMLImageElement>;
    @ViewChild('leftImage') private readonly leftImageRef: ElementRef<HTMLImageElement>;
    @ViewChild('rightImage') private readonly rightImageRef: ElementRef<HTMLImageElement>;

    @ViewChild('middleImageDiv') private readonly middleImageDivRef: ElementRef<HTMLDivElement>;
    @ViewChild('leftImageDiv') private readonly leftImageDivRef: ElementRef<HTMLDivElement>;
    @ViewChild('rightImageDiv') private readonly rightImageDivRef: ElementRef<HTMLDivElement>;

    ngAfterViewInit() {
        this.videoLogService.refreshViewer.subscribe(() => {
            this.cdr.detectChanges();
        });
        this.videoLogService.imagedToggled.subscribe((imageName) => {
            switch (imageName) {
                case 'Left':
                    this.showLeft = !this.showLeft;
                    break;
                case 'Center':
                    this.showCenter = !this.showCenter;
                    break;
                case 'Right':
                    this.showRight = !this.showRight;
                    break;
                default:
                    break;
            }
        });

        this.videoLogService.openThreeImageView.subscribe((open: boolean) => {
            if (!this.threeImageViewEnabled) {
                this.hideSwapBtn = true;
            } else if (!this.showThreeImages && open) {
                this.swapPanels();
                this.hideSwapBtn = false;
                this.resetImages();
            } else if (this.showThreeImages && !open) {
                this.swapPanels();
                this.hideSwapBtn = true;
            }
        });
    }

    private readonly goToMarker: () => void;

    async activate() {
        await Promise.all([this.loadESRIClasses()]);
        if (!this.mapViewRef) {
            this.mapViewRef = await this.setupMapViewRef(this.mapViewRef);
        }

        //the markerSymbol object will be empty if no input was given from the client app
        if (JSON.stringify(this.markerSymbol) === '{}') {
            if (this.directionalMarker) {
                this.markerSymbol = {
                    type: 'simple-marker',
                    style: 'triangle',
                    color: 'red',
                    angle: 180,
                    size: 15
                };
            } else {
                this.markerSymbol = {
                    type: 'simple-marker',
                    style: 'circle'
                };
            }
        }

        if (this._lat && this._long) {
            this.setVideoLogViewerMarker(this._lat, this._long);
            this.setVideoLogLocation();
        }

        this.setClickAction();

        this.offsetSubscription = this.videoLogService.newOffset.pipe(
            distinctUntilChanged()
        ).subscribe(async image => {
            if (image) {
                this._lat = image.latitude;
                this._long = image.longitude;
                this.setVideoLogViewerMarker(this._lat, this._long);

                if (this.aoiString && this.aoiString !== '') {
                    //when videolog's aoi is used, some locaitons lat / long does not always find the right crs.
                    //Increasing this buffer helps. Subsequently, on videolog side, when aoi is used, it will look through crsList and match crs to get correct result.
                    //This matching is also done below in setVideoLogLocation() when aoi is used to set location.
                    this.buffer = 100;
                }

                let rmsOffsetsPromise: Promise<any> | CRSList;
                if (this.videoLogService.crsSelection) {
                    rmsOffsetsPromise = this.gisToolsService.getOffsetForLatLong(this._lat, this._long, this.buffer, 'latLong { latitude, longitude }, countyCode, stateRouteNumber, routeName, segmentNumber, offset, direction, distance, juris');
                } else {
                    rmsOffsetsPromise = [{
                        countyCode: this.videoLogService.videoLog.countyCode,
                        direction: this.videoLogService.videoLog.direction,
                        distance: null,
                        latLong: { latitude: this._lat, longitude: this._long },
                        offset: image.offset,
                        routeName: this.videoLogService.videoLog.routeName,
                        segmentNumber: this.videoLogService.videoLog.segmentNumber,
                        stateRouteNumber: this.videoLogService.videoLog.stateRouteNumber
                    }];
                }

                const [rmsOffsets, rmsLocalOffsets] = await Promise.all([
                    rmsOffsetsPromise,
                    !this.videoLogService.findinxlist ? [] : this.gisToolsService.getNearbyLocalIntersections(this._lat, this._long, this.buffer, 'countyCode, countyIXN, stateRouteNumber, streetName, streetNameIXN, stateRouteIXN, intersectionType')
                ]);

                let crsList: CRSList = [];
                if (rmsOffsets && rmsOffsets.length > 0) {
                    crsList = rmsOffsets.filter(x => !['4', '8'].includes(x.juris));
                } else {
                    crsList.push({ countyCode: '', direction: '', distance: 0, latLong: { latitude: 0, longitude: 0 }, offset: '', routeName: '', segmentNumber: '', stateRouteNumber: '' });
                }

                let localcrslist: LocalCRSList = [];
                if (rmsLocalOffsets && rmsLocalOffsets.length > 0) {
                    for (const route of rmsLocalOffsets) {
                        if (route.stateRouteIXN === '0000') {
                            //local roads will have stateRouteIXN = "0000"
                            localcrslist.push(route);
                        }
                    }
                } else {
                    localcrslist = [];
                }

                this.buffer = 50;
                this.aoiString = '';

                this.positionChange.next({
                    lat: this._lat,
                    long: this._long,
                    crsList,
                    localCRSList: localcrslist
                });
            }
            this.cdr.detectChanges();
        });

        this._active = true;
    }

    deactivate() {
        this._lat = null;
        this._long = null;
        this.videoLogService.resetVideoLog();
        if (this.videoLogViewerHandler) {
            this.videoLogViewerHandler.remove();
        }

        if (this.markerGraphic) {
            this.mapViewRef.graphics.remove(this.markerGraphic);
            this.markerGraphic.destroy();
            this.markerGraphic = null;
        }
        if (this.offsetSubscription) {
            this.offsetSubscription.unsubscribe();
            this.offsetSubscription = null;
        }
        this._active = false;
    }

    async loadESRIClasses() {
        if (!Graphic || !WebMercatorUtils) {
            [Graphic, WebMercatorUtils] = await Promise.all([ESRI.Graphic, ESRI.WebMercatorUtils]);
        }
    }

    setClickAction() {
        if (this.mapClickActive) {
            this.videoLogViewerHandler = this.mapViewRef.on('click', event => {
                const geographicPoint = <__esri.Point>WebMercatorUtils.webMercatorToGeographic(event.mapPoint);
                this._lat = parseFloat(geographicPoint.y.toFixed(this.precision));
                this._long = parseFloat(geographicPoint.x.toFixed(this.precision));
                this.firstZoom = true;
                this.onPositionChange(true);
            });
        } else if (this.videoLogViewerHandler) {
            this.videoLogViewerHandler.remove();
        }
    }

    onPositionChange(fromClick?: boolean) {
        if (this.active) {
            this.setVideoLogViewerMarker(this._lat, this._long);
            this.setVideoLogLocation(fromClick);
        }
    }

    setVideoLogViewerMarker(latitude, longitude) {
        const point = {
            type: 'point',
            latitude,
            longitude
        };
        if (this.markerGraphic) {
            const newGeometry = this.markerGraphic.geometry.clone() as __esri.Point;
            newGeometry.latitude = latitude;
            newGeometry.longitude = longitude;
            this.markerGraphic.geometry = newGeometry;
        } else {
            this.markerGraphic = new Graphic({
                geometry: point as __esri.GeometryProperties,
                symbol: this.markerSymbol as __esri.SymbolProperties
            });
            this.mapViewRef.graphics.add(this.markerGraphic);
        }

        if (this.directionalMarker && this.videoLogService.image) {
            if (this.videoLogService.images[this.videoLogService.image.sequenceNumber + 1]) {
                //update marker angle
                const curLat: number = latitude;
                const curLong: number = longitude;
                const nextLat: number = this.videoLogService.images[this.videoLogService.image.sequenceNumber + 1].latitude;
                const nextLong: number = this.videoLogService.images[this.videoLogService.image.sequenceNumber + 1].longitude;
                this.calculateAndSetMarkerAngle(curLat, curLong, nextLat, nextLong, point);
            } else {
                //use slope for prev offset when at the end of images, not accurate, but fine for only one offset
                const curLat: number = this.videoLogService.images[this.videoLogService.image.sequenceNumber - 1].latitude;
                const curLong: number = this.videoLogService.images[this.videoLogService.image.sequenceNumber - 1].longitude;
                const nextLat: number = latitude;
                const nextLong: number = longitude;
                this.calculateAndSetMarkerAngle(curLat, curLong, nextLat, nextLong, point);
            }
        }

        if ((typeof this.zoomOnPositionChange === 'boolean' && this.zoomOnPositionChange)
            || (typeof this.zoomOnPositionChange === 'string' && this.zoomOnPositionChange && this.zoomOnPositionChange !== 'none')) {
            this.goToMarker();
        }
    }

    calculateAndSetMarkerAngle(curLat: number, curLong: number, nextLat: number, nextLong: number, point: {type: string; latitude: number; longitude: number}) {
        const rise = nextLong - curLong;
        const run = nextLat - curLat;
        let angle = Math.atan2(rise, run);
        angle *= 180 / Math.PI;
        angle = angle < 0 ? angle + 360 : angle;
        this.markerSymbol.angle = angle;

        this.mapViewRef.graphics.remove(this.markerGraphic);

        this.markerGraphic = new Graphic({
            geometry: point as __esri.GeometryProperties,
            symbol: this.markerSymbol as __esri.SymbolProperties
        });
        this.mapViewRef.graphics.add(this.markerGraphic);
    }

    async setVideoLogLocation(fromClick?: boolean) {
        const locations = await this.gisToolsService.getOffsetForLatLong(this._lat, this._long, this.buffer, 'countyCode, stateRouteNumber, segmentNumber, offset');
        const prevYear: number = this.videoLogService.currentYear ? this.videoLogService.currentYear : null;
        this.videoLogService.resetVideoLog();

        let index = 0;
        let aoiArr = [];
        if (this.aoiString &&  this.aoiString !== '') {
            aoiArr = this.aoiString.split('|');
            const cty = aoiArr[0] ? aoiArr[0] : '';
            const rt = aoiArr[1] ? aoiArr[1] : '';
            const segStart = aoiArr[2] ? aoiArr[2] : '';

            let count = 0;
            for (const location of locations) {
                if (location.countyCode === cty) {
                    if (rt === '') {
                        index = count;
                        break;
                    } else if (location.stateRouteNumber === rt) {
                        if (segStart === '') {
                            index = count;
                            break;
                        } else if (location.segmentNumber === segStart) {
                            index = count;
                            break;
                        }
                    }
                }
                count++;
            }
        }

        if (locations && locations.length > 0) {
            await this.videoLogService.goToSegmentAndSetOffset(locations[index].countyCode, locations[index].stateRouteNumber, locations[index].segmentNumber, locations[index].offset, fromClick);
            if (this.videoLogService.showHistoricVideoLog && prevYear && this.videoLogService.yearRange
                && this.videoLogService.yearRange.years.includes(prevYear)) {
                this.videoLogService.currentYear = prevYear;
                this.loadHistoricReport.emit();
            }
        }
        if (this.videoLogService.image) {
            if (fromClick) {
                this.firstZoom = true;
            }
            this.setVideoLogViewerMarker(this.videoLogService.image.latitude, this.videoLogService.image.longitude);

            if (!this.dragEventsSet) {
                this.middleImageRef.nativeElement.addEventListener('dragstart', (event) => {this.handleDragStart(event); });
                this.middleImageRef.nativeElement.addEventListener('dragend', () => this.handleDragEnd());
                this.middleImageRef.nativeElement.addEventListener('dragover', (event) => this.handleDragOver(event));
                this.middleImageRef.nativeElement.addEventListener('drop', (event) => { this.handleDrop(event); });
                this.middleImageRef.nativeElement.addEventListener('drag', (event) => { this.handleDrag(event); });

                this.leftImageRef.nativeElement.addEventListener('dragstart', (event) => { this.handleDragStart(event); });
                this.leftImageRef.nativeElement.addEventListener('dragend', () => this.handleDragEnd());
                this.leftImageRef.nativeElement.addEventListener('dragover', (event) => this.handleDragOver(event));
                this.leftImageRef.nativeElement.addEventListener('drop', (event) => { this.handleDrop(event); });
                this.leftImageRef.nativeElement.addEventListener('drag', (event) => { this.handleDrag(event); });

                this.rightImageRef.nativeElement.addEventListener('dragstart', (event) => { this.handleDragStart(event); });
                this.rightImageRef.nativeElement.addEventListener('dragend', () => this.handleDragEnd());
                this.rightImageRef.nativeElement.addEventListener('dragover', (event) => this.handleDragOver(event));
                this.rightImageRef.nativeElement.addEventListener('drop', (event) => { this.handleDrop(event); });
                this.rightImageRef.nativeElement.addEventListener('drag', (event) => { this.handleDrag(event); });

                this.dragEventsSet = true;
            }
        }

        this.cdr.detectChanges();
    }

    @Input() public setCrs = async (crs) => {
        this.videoLogService.resetVideoLog();
        await this.videoLogService.goToSegmentAndSetOffset(crs.countyCode, crs.stateRouteNumber, crs.segmentNumber, crs.offset);

        if (this.videoLogService.image && this.enablePopUpWindow) { // popout window doesn't need to update marker
            this.setVideoLogViewerMarker(this.videoLogService.image.latitude, this.videoLogService.image.longitude);
            this._lat = this.videoLogService.image.latitude;
            this._long = this.videoLogService.image.longitude;
        }
        this.cdr.detectChanges();
    }

    openPopUpWindow(pos: string) {
        if (this.enablePopUpWindow) {
            //send event to host app to open popup window
            if (pos === 'm' && !this.showThreeImages) {
                this.openPopUp.emit();
            } else {
                this.openPopUp.emit(pos);
            }
        }
    }

    @Input() public swapPanels = (isPopUp = false) => {
        if (this.threeImageViewEnabled) {
            if (!isPopUp) {
                this.swapPanelEvent.emit();
            }
            this.videoLogService.swapPanelUpdate(this.showThreeImages);
            this.showThreeImages = !this.showThreeImages;
            this.topPx = !this.showThreeImages ? '25px' : '';
            this.bottomPx = !this.showThreeImages ? '' : '0px';
            this.leftImageDivRef.nativeElement.style.resize = !this.showThreeImages ? 'none' : 'both';
            this.rightImageDivRef.nativeElement.style.resize = !this.showThreeImages ? 'none' : 'both';
            this.middleImageDivRef.nativeElement.style.width = !this.showThreeImages ? '100%' : '33%';

            if (isPopUp) {
                this.hidePopUpButtons = true;
                this.handlePopUp();
            }
        }
    }
    handleDragStart(e) {
        this.isDragging = true;
        this.initialX = e.clientX;
        this.initialY = e.clientY;

        if (e.target instanceof HTMLDivElement || e.target instanceof HTMLImageElement) {
            this.imageBeingDragged = e.target instanceof HTMLDivElement ? e.target : e.target.parentElement;

            const emptyImage = document.createElement('img');
            // Set the src to be a 0x0 gif
            emptyImage.src = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
            e.dataTransfer.setDragImage(emptyImage, 0, 0);
        }
    }
    handleDragEnd() {
        if (this.isDragging) {
            this.isDragging = false;
        }
        this.imageBeingDragged.style.boxShadow = '';
    }
    handleDrag(e) {
        if (this.isDragging) {
            const deltaX = e.clientX - this.initialX;
            const deltaY = e.clientY - this.initialY;

            this.initialX = e.clientX;
            this.initialY = e.clientY;

            if (e.target instanceof HTMLDivElement || e.target instanceof HTMLImageElement) {
                this.imageBeingDragged = e.target instanceof HTMLDivElement ? e.target : e.target.parentElement;
                const currentX = parseFloat(getComputedStyle(this.imageBeingDragged).left) || 0;
                const currentY = parseFloat(getComputedStyle(this.imageBeingDragged).top) || 0;

                this.renderer.setStyle(this.imageBeingDragged, 'left', `${currentX + deltaX}px`);
                this.renderer.setStyle(this.imageBeingDragged, 'top', `${currentY + deltaY}px`);

                const imageIndex = this.imageBeingDragged === this.leftImageDivRef.nativeElement ? 0 : this.imageBeingDragged === this.middleImageDivRef.nativeElement ? 1 : 2;
                this.moveImageToTop(imageIndex);
                this.imageBeingDragged.style.boxShadow = '0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19)';
            }
        }
    }
    handleDragOver(e) {
        e.preventDefault();
        return false;
    }
    handleDrop(e) {
        e.stopPropagation(); // stops the browser from redirecting.
        this.imageBeingDragged.style.boxShadow = '';
        return false;
    }

    resetImages() {
        this.showLeft = true;
        this.showCenter = true;
        this.showRight = true;
        this.videoLogService.resetImageToggleButtons();

        this.prevX = [0, 0, 0];
        this.prevY = [0, 0, 0];
        this.currentScale = [1, 1, 1];

        this.leftImageRef.nativeElement.style.transform = 'none';
        this.leftImageRef.nativeElement.style.transformOrigin = '328px 188px';
        this.leftImageDivRef.nativeElement.style.top = '5px';
        this.leftImageDivRef.nativeElement.style.left = '5px';
        this.leftImageDivRef.nativeElement.style.height = 'auto';
        this.leftImageDivRef.nativeElement.style.width = '33%';

        this.middleImageRef.nativeElement.style.transform = 'none';
        this.middleImageRef.nativeElement.style.transformOrigin = !this.showThreeImages ? '' : '328px 188px';
        this.middleImageDivRef.nativeElement.style.top = !this.showThreeImages ? '' : '5px';
        this.middleImageDivRef.nativeElement.style.left = !this.showThreeImages ? '' : '0';
        this.middleImageDivRef.nativeElement.style.right = !this.showThreeImages ? '' : '0';
        this.middleImageDivRef.nativeElement.style.height = !this.showThreeImages ? '100%' : 'auto';
        this.middleImageDivRef.nativeElement.style.width = !this.showThreeImages ? '100%' : '33%';

        this.rightImageRef.nativeElement.style.transform = 'none';
        this.rightImageRef.nativeElement.style.transformOrigin = '328px 188px';
        this.rightImageDivRef.nativeElement.style.top = '5px';
        this.rightImageDivRef.nativeElement.style.right = '5px';
        this.rightImageDivRef.nativeElement.style.left = '';
        this.rightImageDivRef.nativeElement.style.height = 'auto';
        this.rightImageDivRef.nativeElement.style.width = '33%';

    }

    zoomImage(e: WheelEvent, imageNumber: number) {
        e.preventDefault();
        const target = e.target;
        if (target instanceof HTMLImageElement) {
            let x = this.prevX[imageNumber], y = this.prevY[imageNumber];
            if (this.currentScale[imageNumber] > 1  && this.currentScale[imageNumber] < 3) {
                x = e.clientX - target.x;
                this.prevX[imageNumber] = x;
                y = e.clientY - target.y;
                this.prevY[imageNumber] = y;
            }
            this.currentScale[imageNumber] += e.deltaY > 0 ? -this.zoomFactor : this.zoomFactor;
            // Set maximum and minimum zoom level
            this.currentScale[imageNumber] = Math.max(1, Math.min(3, this.currentScale[imageNumber]));

            //last scrolled image will be on top of others
            this.moveImageToTop(imageNumber);

            target.style.transformOrigin = `${x}px ${y}px`;
            target.style.transform = `scale(${this.currentScale[imageNumber]})`;
        }
    }

    moveImageToTop(imageNumber: number) {
        switch (imageNumber) {
            case 0:
                this.leftImageDivRef.nativeElement.style.zIndex = '11';
                this.middleImageDivRef.nativeElement.style.zIndex = '10';
                this.rightImageDivRef.nativeElement.style.zIndex = '9';
                break;
            case 1:
                this.leftImageDivRef.nativeElement.style.zIndex = '10';
                this.middleImageDivRef.nativeElement.style.zIndex = '11';
                this.rightImageDivRef.nativeElement.style.zIndex = '10';
                break;
            case 2:
                this.leftImageDivRef.nativeElement.style.zIndex = '10';
                this.middleImageDivRef.nativeElement.style.zIndex = '10';
                this.rightImageDivRef.nativeElement.style.zIndex = '11';
                break;
            default:
                break;
        }
    }

    handlePopUp() {
        if (this.videoLogService.image) {
            const url = new URL(window.location.href);
            const pos: string = url.searchParams.get('pos');
            this.showThreeImages = false;
            //if pos is 'm' do nothing
            if (pos === 'l') {
                this.videoLogService.image.url = this.videoLogService.image.lurl;
            } else if (pos === 'r') {
                this.videoLogService.image.url = this.videoLogService.image.rurl;
            }

            this.leftImageDivRef.nativeElement.style.top = '';
            this.leftImageDivRef.nativeElement.style.left = '';
            this.leftImageDivRef.nativeElement.style.height = '';
            this.leftImageDivRef.nativeElement.style.width = '';

            this.middleImageDivRef.nativeElement.style.height = '100%';
            this.middleImageDivRef.nativeElement.style.width = '100%';

            this.rightImageDivRef.nativeElement.style.top = '';
            this.rightImageDivRef.nativeElement.style.right = '';
            this.rightImageDivRef.nativeElement.style.left = '';
            this.rightImageDivRef.nativeElement.style.height = '';
            this.rightImageDivRef.nativeElement.style.width = '';
            //also disable the swapPanelBtn
            this.hideSwapBtn = true;
        }
    }
}
